// shared/src/resolvers/llm-enum-coerce.ts
// 2026-05-29 — translate LLM-emitted ENUM values to canonical form at the
// boundary, instead of hard-failing the model against a rigid z.enum().
//
// Principle (standing user rule): NEVER artificially constrain the LLM with a
// rigid enum + hard reject. The model emits its natural output ("Medium",
// "moderate", "bullish"); a resolver maps it to the canonical value in code
// before validation. Mirrors canonicalizeIndicator() / normalizeMacroPrediction
// Mode() — the same translate-in-code pattern, now for enum-valued fields.
//
// Why: an Analyst Q&A answer hard-failed with `Zod parse failed: expected
// 'HIGH' | 'MED' | 'LOW'` because the model emitted a confidence value outside
// that exact casing/spelling. The brief/question is otherwise perfect — only
// the rigid enum rejected it. See docs/llm-enum-coercion-audit-2026-05-29.md.
//
// Fail-loud preserved (per [[feedback_no_fallbacks]]): an UNKNOWN value passes
// through UNCHANGED so the wrapping z.enum still rejects it — we coerce known
// synonyms/casing, we never invent a default for a genuinely unrecognized
// value.

import { z } from 'zod';

/**
 * Build a case-insensitive alias→canonical coercer. Alias keys are matched
 * trimmed + lowercased. Non-string / empty / unknown input is returned
 * unchanged so the wrapping z.enum still fail-louds on it.
 */
export function makeEnumCoercer(
  aliases: Record<string, string>,
): (input: unknown) => unknown {
  const map: Record<string, string> = {};
  for (const [k, v] of Object.entries(aliases)) map[k.trim().toLowerCase()] = v;
  return (input: unknown): unknown => {
    if (typeof input !== 'string') return input;
    const key = input.trim().toLowerCase();
    if (key.length === 0) return input;
    const canonical = map[key];
    return canonical !== undefined ? canonical : input;
  };
}

/**
 * Wrap a z.enum with a pre-coercion pass. The model's natural output is
 * translated to canonical BEFORE the enum check; unknowns still reject.
 * `z.infer` of the result is the SAME union as the bare enum, so downstream
 * types are unchanged.
 */
export function coercedEnum<U extends string, T extends readonly [U, ...U[]]>(
  values: T,
  coerce: (input: unknown) => unknown,
) {
  // Mirror z.enum's own generic (U extends string, T extends readonly [U,...U[]])
  // so the literal tuple is captured and z.infer resolves to the literal union
  // (NOT `string`). A plain `T extends [string,...string[]]` widens the array to
  // string[] → z.infer became `string`, breaking downstream typed consumers
  // (e.g. MacroPredictionEmit.predicted_observables[].direction).
  return z.preprocess(coerce, z.enum(values));
}

/**
 * LENIENT variant of {@link coercedEnum} for NON-AUTHORITATIVE metadata enums
 * (horizon, regime, thesis_div, thesis_status). Coerces known synonyms to
 * canonical; a STILL-unknown value is LOGGED (`console.warn`, visible via
 * `wrangler tail`) and PASSED THROUGH unchanged instead of hard-failing — so a
 * single odd LLM metadata value can never abort an entire brief. The raw value
 * also survives in the stored stage-emit JSON for later review.
 *
 * z.infer is `string` (the canonical members remain the common case). Do NOT
 * use for MATERIAL fields (direction, scored confidence) — those keep
 * coercedEnum / z.enum so a wrong value fail-louds. Introduced 2026-06-01 after
 * a single thesis `horizon: "1d"` SchemaError killed a whole scheduled brief
 * (019e8365) — see docs/audits/LEARNINGS.md C029.
 */
export function lenientEnum<U extends string>(
  values: readonly [U, ...U[]],
  coerce: (input: unknown) => unknown,
  label: string,
) {
  const canonical = new Set<string>(values);
  // minimal console interface inline — console.warn exists in both Node + Workers
  const log = (globalThis as { console?: { warn(msg: string): void } }).console;
  return z.preprocess((input) => {
    const c = coerce(input);
    if (typeof c !== 'string') {
      log?.warn(`[enum-coerce] ${label}: non-string value ${JSON.stringify(c)} — coerced to "" (non-fatal)`);
      return '';
    }
    if (!canonical.has(c)) {
      log?.warn(`[enum-coerce] ${label}: out-of-vocab "${c}" — preserved raw, brief continues (non-fatal)`);
    }
    return c;
  }, z.string());
}

// ─── Canonical coercers per LLM enum family ──────────────────────────────

/**
 * HIGH / MED / LOW — used for confidence, conviction, thesis_div, and any
 * other 3-bucket qualitative scale the model emits. Accepts the common
 * spellings/casings the model drifts to (the failure was a value outside the
 * exact 'HIGH'|'MED'|'LOW' set).
 */
export const coerceConfidenceBucket = makeEnumCoercer({
  high:          'HIGH',
  hi:            'HIGH',
  h:             'HIGH',
  strong:        'HIGH',
  'very high':   'HIGH',
  'medium-high': 'HIGH',
  med:           'MED',
  medium:        'MED',
  mid:           'MED',
  moderate:      'MED',
  m:             'MED',
  'medium-low':  'MED',
  low:           'LOW',
  lo:            'LOW',
  l:             'LOW',
  weak:          'LOW',
  'very low':    'LOW',
});

/** HIGH/MED/LOW schema — its ONLY consumer is the non-authoritative
 *  `thesis_div` metadata field (verified 2026-06-01: 0 worker uses, 5 m.*
 *  prompt uses), so it is LENIENT (log + preserve-raw, never brief-fatal).
 *  If a MATERIAL HIGH/MED/LOW field is ever added (e.g. a scored confidence
 *  bucket that feeds calibration), give it a SEPARATE strict `coercedEnum`
 *  rather than reusing this one. */
export const HighMedLowEnum = lenientEnum(['HIGH', 'MED', 'LOW'], coerceConfidenceBucket, 'thesis_div');

/**
 * direction — `up` / `down` / `flat`. The model drifts to bullish/bearish/
 * neutral, casing, and ±/rising/falling. Mapped to the lowercase canonical.
 * (p-arbiter's set is `up`/`down` only — a `flat`-mapped value correctly
 * fail-louds there, which is the intended V3 'flat dropped' behaviour.)
 */
export const coerceDirection = makeEnumCoercer({
  up: 'up', bull: 'up', bullish: 'up', long: 'up', rise: 'up', rising: 'up',
  higher: 'up', positive: 'up', gain: 'up', up_side: 'up', upside: 'up',
  down: 'down', bear: 'down', bearish: 'down', short: 'down', fall: 'down',
  falling: 'down', lower: 'down', negative: 'down', decline: 'down',
  drop: 'down', downside: 'down', down_side: 'down',
  flat: 'flat', neutral: 'flat', sideways: 'flat', unchanged: 'flat',
  rangebound: 'flat', 'range-bound': 'flat', stable: 'flat', steady: 'flat',
});
export const DirectionEnum = coercedEnum(['up', 'down', 'flat'], coerceDirection);

/** horizon — `1d` / `1w` / `2w` / `1m` / `1q` time-bucket. Normalizes
 *  word/spacing drift (`1 week`, `1week`, `1mo`, `1 quarter`). `1d` added
 *  2026-06-01 (LLM legitimately emits a fast macro horizon; was brief-fatal —
 *  C029). Sub-day phrasings normalize to `1d`. LENIENT: any value still
 *  outside the set is logged + preserved, never brief-fatal. */
export const coerceHorizon = makeEnumCoercer({
  '1d': '1d', '1day': '1d', '1 day': '1d', 'one day': '1d', '1dy': '1d', '24h': '1d', 'daily': '1d',
  '1w': '1w', '1week': '1w', '1 week': '1w', 'one week': '1w', '1wk': '1w', '1 wk': '1w',
  '2w': '2w', '2week': '2w', '2 weeks': '2w', '2wk': '2w', '2 wk': '2w', 'two weeks': '2w',
  '1m': '1m', '1month': '1m', '1 month': '1m', '1mo': '1m', '1 mo': '1m', 'one month': '1m',
  '1q': '1q', '1quarter': '1q', '1 quarter': '1q', '1qtr': '1q', 'one quarter': '1q',
  '3m': '1q', '3month': '1q', '3 months': '1q', '3mo': '1q',
});
export const HorizonEnum = lenientEnum(['1d', '1w', '2w', '1m', '1q'], coerceHorizon, 'horizon');

/** thesis_status — IN_PLAY / WATCH / INVALIDATED. */
export const coerceThesisStatus = makeEnumCoercer({
  in_play: 'IN_PLAY', 'in play': 'IN_PLAY', inplay: 'IN_PLAY', 'in-play': 'IN_PLAY',
  active: 'IN_PLAY', live: 'IN_PLAY',
  watch: 'WATCH', watching: 'WATCH', watchlist: 'WATCH', monitor: 'WATCH', monitoring: 'WATCH',
  invalidated: 'INVALIDATED', invalid: 'INVALIDATED', broken: 'INVALIDATED',
  dead: 'INVALIDATED', failed: 'INVALIDATED', void: 'INVALIDATED',
});
export const ThesisStatusEnum = lenientEnum(['IN_PLAY', 'WATCH', 'INVALIDATED'], coerceThesisStatus, 'thesis_status');

/** regime — risk-on / risk-off / mixed / transitioning (hyphen + case sensitive). */
export const coerceRegime = makeEnumCoercer({
  'risk-on': 'risk-on', 'risk on': 'risk-on', risk_on: 'risk-on', riskon: 'risk-on',
  'risk-off': 'risk-off', 'risk off': 'risk-off', risk_off: 'risk-off', riskoff: 'risk-off',
  mixed: 'mixed', balanced: 'mixed', choppy: 'mixed',
  transitioning: 'transitioning', transition: 'transitioning', transitional: 'transitioning',
  shifting: 'transitioning', 'in transition': 'transitioning',
});
export const RegimeEnum = lenientEnum(['risk-on', 'risk-off', 'mixed', 'transitioning'], coerceRegime, 'regime');

/** thesis_align (p-arbiter) — ALIGNED / DIVERGENT / NEUTRAL. */
export const coerceThesisAlign = makeEnumCoercer({
  aligned: 'ALIGNED', aligns: 'ALIGNED', align: 'ALIGNED', 'in line': 'ALIGNED',
  matches: 'ALIGNED', concordant: 'ALIGNED',
  divergent: 'DIVERGENT', diverges: 'DIVERGENT', diverge: 'DIVERGENT',
  divergence: 'DIVERGENT', opposed: 'DIVERGENT', opposing: 'DIVERGENT', contrary: 'DIVERGENT',
  neutral: 'NEUTRAL', none: 'NEUTRAL', ambiguous: 'NEUTRAL',
});
export const ThesisAlignEnum = coercedEnum(['ALIGNED', 'DIVERGENT', 'NEUTRAL'], coerceThesisAlign);

/** ticker_alignment (p-stage) — aligns_market / diverges_market / neutral. */
export const coerceTickerAlignment = makeEnumCoercer({
  aligns_market: 'aligns_market', 'aligns market': 'aligns_market', aligns: 'aligns_market',
  aligned: 'aligns_market', 'aligns with market': 'aligns_market', align: 'aligns_market',
  'aligned with market': 'aligns_market', 'aligns-market': 'aligns_market',
  diverges_market: 'diverges_market', 'diverges market': 'diverges_market', diverges: 'diverges_market',
  divergent: 'diverges_market', 'diverges from market': 'diverges_market', diverge: 'diverges_market',
  'diverges-market': 'diverges_market',
  neutral: 'neutral', none: 'neutral', ambiguous: 'neutral',
});
export const TickerAlignmentEnum = coercedEnum(['aligns_market', 'diverges_market', 'neutral'], coerceTickerAlignment);

/** prediction_mode (p-refine direct field — NOT routed through
 *  normalizeMacroPredictionMode) — catalyst / trend. */
export const coercePredictionMode = makeEnumCoercer({
  catalyst: 'catalyst', event: 'catalyst', 'event-driven': 'catalyst',
  event_driven: 'catalyst', 'event driven': 'catalyst', catalytic: 'catalyst',
  trend: 'trend', trending: 'trend', drift: 'trend', 'trend-following': 'trend',
  continuation: 'trend',
});
export const PredictionModeEnum = coercedEnum(['catalyst', 'trend'], coercePredictionMode);

/** crux_match (quality.crux-scorer) — contrarian / consensus / neither. */
export const coerceCruxMatch = makeEnumCoercer({
  contrarian: 'contrarian', contra: 'contrarian', 'contrarian path': 'contrarian',
  consensus: 'consensus', 'consensus path': 'consensus',
  neither: 'neither', none: 'neither', both: 'neither', 'no match': 'neither',
  no_match: 'neither',
});
export const CruxMatchEnum = coercedEnum(['contrarian', 'consensus', 'neither'], coerceCruxMatch);

/**
 * cite (skeptic disconfirming_evidence tag). m-stage uses `observable`;
 * p-stage uses `ticker_data` + `momentum`. Case-folds + space→underscore +
 * unambiguous synonyms. Returns the canonical token; if it isn't in a given
 * file's value set the wrapping enum fail-louds (correct — e.g. an m-stage
 * `ticker_data` is genuinely off-vocabulary there).
 */
export const coerceCiteTag = makeEnumCoercer({
  analyst_output: 'analyst_output', 'analyst output': 'analyst_output',
  analyst: 'analyst_output', analyst_view: 'analyst_output', analyst_draft: 'analyst_output',
  opposing_draft: 'opposing_draft', 'opposing draft': 'opposing_draft', opposing: 'opposing_draft',
  opposing_path: 'opposing_draft', cross_fed: 'opposing_draft', 'cross-fed': 'opposing_draft',
  counter_draft: 'opposing_draft',
  factual_error: 'factual_error', 'factual error': 'factual_error', factual: 'factual_error',
  factual_check: 'factual_error', error: 'factual_error', fact_error: 'factual_error',
  reasonability: 'reasonability', reasoning: 'reasonability', logic: 'reasonability',
  reasonableness: 'reasonability', plausibility: 'reasonability', coherence: 'reasonability',
  macro: 'macro', macro_data: 'macro', macro_payload: 'macro',
  news: 'news', news_data: 'news', headline: 'news',
  observable: 'observable', observables: 'observable', observable_plausibility: 'observable',
  ticker_data: 'ticker_data', 'ticker data': 'ticker_data', price_data: 'ticker_data',
  ticker: 'ticker_data',
  momentum: 'momentum', momentum_signal: 'momentum', technicals: 'momentum',
});
